import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { ApiTokenGenerateModal } from "@/pages/settings/api-tokens/widgets/ApiTokenGenerateModal.tsx";
import { useApiUserSave } from "@/queries/settings/use-api-users.query.ts";
import { ApiApiUser } from "@/types/api-user.types.ts";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface ApiTokenEditModalProps extends ModalProps {
  apiUser?: ApiApiUser;
}

export function ApiTokenEditModal({ apiUser, ...props }: ApiTokenEditModalProps) {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const apiTokenSchema = z.object({
    name: z.string().min(1, t("errors:field_required")),
  });

  type ApiTokenSchema = z.infer<typeof apiTokenSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ApiTokenSchema>({
    mode: "onSubmit",
    resolver: zodResolver(apiTokenSchema),
    defaultValues: { name: apiUser?.name ?? "" },
  });

  const apiUserSave = useApiUserSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const savedUser = await apiUserSave.mutateAsync({
        id: apiUser?.id ?? 0,
        payload: { name: data.name },
      });
      close(id);
      if (!apiUser) {
        openModal(ApiTokenGenerateModal, { apiUser: savedUser });
      }
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={apiUser ? t("core:service_user_edit") : t("core:service_user_create")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={apiUserSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          required={true}
          label={t("core:name")}
          autoFocus={true}
          type="text"
          {...register("name")}
          error={errors.name?.message}
        />
      </form>
    </Modal>
  );
}
